import { Injectable, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';

import { ItemRow, LeftSidebarManagementService, StatusLhpItem } from '../../../Common/Layout/services/left-sidebar-management.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { AmplifyLeftSidebarService } from './amplify-left-sidebar.service';
import { WireService } from '../../Wire/services/wire.service';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { WireListItemModel } from '../../Wire/models/wire-list-item.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';

@Injectable()
export class AmplifyLeftSidebarManagementService extends LeftSidebarManagementService implements OnDestroy {

    protected readonly wireStatusFilter = new FilterTypeCheckboxModel<ReleaseStatus>(
        'status',
        'Amplify.leftSidebar.wire.filter.status',
        [
            {
                label: 'Amplify.filter.status.draft',
                value: ReleaseStatus.draft
            },
            {
                label: 'Amplify.filter.status.submitted',
                value: ReleaseStatus.submitted
            },
            {
                label: 'Amplify.filter.status.scheduled',
                value: ReleaseStatus.scheduled
            },
            {
                label: 'Amplify.filter.status.published',
                value: ReleaseStatus.published
            },
            {
                label: 'Amplify.filter.status.retracted',
                value: ReleaseStatus.retracted
            },
            {
                label: 'Amplify.filter.status.failed',
                value: ReleaseStatus.failed
            },
        ]
    );

    constructor(
        usersService: UsersService,
        public sidebarService: AmplifyLeftSidebarService,
        public wireService: WireService,
        public messageService: MessageService,
        public displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService
    ) {
        super(usersService, sidebarService, displayGroupsService, localizationService);

        this.filters.push(this.wireStatusFilter);

        this.columnsProvider$.next([
            this.itemNameColumn,
            this.statusCustomColumnModel,
            ...this.metadataColumns
        ]);

        this.subscriptions.push(
            this.wireService.all$.subscribe(
                (items) => {
                    this.allListItems = items;
                    this.setUpFilters();
                }
            )
        );
    }

    public setCustomData(rowData: ItemRow[]): ItemRow[] {
        this.setStatusData(rowData);
        return rowData;
    }

    setUpFilters() {
        this.sidebarService.getWireStatusTypeFilterCounts(this.allListItems as WireListItemModel[], this.wireStatusFilter);
        this.getAuditableFilterOptions(this.allListItems);
        this.setFilters(this.filters);
        this.applyFilters();
    }

    applyFilters(): void {
        if (!this.allListItems) {
            return;
        }

        let filteredItems = super.applyCommonFilters();

        filteredItems = this.sidebarService.applyStatusFilters(filteredItems as WireListItemModel[], this.wireStatusFilter);

        this.listItems = filteredItems;
        this.listItemsProvider$.next(this.listItems);
        this.groupItems();
        this.loading$.next(false);
    }

    public handleBulkItemAction(items: AuditableModel[], groups: DisplayGroupModel[], action: ActionsEnum) {
        this.loading$.next(true);
        let ids = items.map(item => item.id);
        switch(action) {
            case ActionsEnum.delete:
                this.wireService.confirmBulkDeleteWire(items, groups).subscribe((result) => {
                    this.loading$.next(false);
                    this.resetSelectionProvider$.next(result);
                });
                break;
            case ActionsEnum.hide:
            case ActionsEnum.show:
                // BUG: https://agility-jira.atlassian.net/browse/NEP-15961 - Can't update visibility of published items
                // Filter out published releases and inform user.
                // TODO remove status filtering when bug is resolved.
                ids = (items as StatusLhpItem[]).filter(x => x.status !== ReleaseStatus.published).map(x => x.id);
                if (ids.length !== items.length) {
                    this.messageService.add({
                        severity: messageSeverityEnum.warning,
                        summary: 'Amplify.dialogs.bulkUpdateWire.error.summary',
                        detail: 'Amplify.dialogs.bulkUpdateWire.warning.detail'
                    });
                }
                if (ids.length) {
                    this.resetSelectionProvider$.next(true);
                    this.wireService.bulkUpdateWireVisibility(ids, action);
                } else {
                    this.loading$.next(false);
                    this.resetSelectionProvider$.next(false);
                }
                break;
            case ActionsEnum.move:
                this.displayGroupsService.openGroupSelectionDialog(null, true).subscribe({
                    next: (group: DisplayGroupModel) => {
                        this.resetSelectionProvider$.next(true);
                        let itemName;
                        if (items.length === 1) {
                            itemName = items[0].name;
                        }
                        this.wireService.updateNewswireGroup(ids, group, itemName);
                    },
                    complete: () => {
                        this.loading$.next(false);
                    }
                });
                break;
            case ActionsEnum.favorite:
                this.wireService.updateNewswireFavorites(ids, []);
                break;
            case ActionsEnum.unfavorite:
                this.wireService.updateNewswireFavorites([], ids);
                break;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
